import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { cn } from './cn';

interface MenuItemSpec {
  key?: string;
  label?: ReactNode;
  icon?: ReactNode;
  danger?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  type?: 'divider';
}

export interface DropdownProps {
  /** The clickable/hoverable trigger. */
  children: ReactNode;
  /** The panel contents (use MenuItem / MenuDivider / MenuLabel). Optional if `menu` is given. */
  panel?: ReactNode;
  /** antd-style declarative menu (`menu={{ items, onClick }}`). */
  menu?: { items?: Array<MenuItemSpec | null | false | undefined>; onClick?: (info: { key: string }) => void };
  /** `hover` suits desktop top-nav groups; `click` suits the user menu. */
  trigger?: 'click' | 'hover' | ('click' | 'hover')[];
  /** Horizontal alignment of the panel against the trigger. */
  align?: 'start' | 'end';
  /** antd `placement` (bottomLeft/bottomRight…) — only the horizontal side is honored. */
  placement?: string;
  disabled?: boolean;
  panelClassName?: string;
  className?: string;
}

/**
 * Headless dropdown / popover (replaces antd Dropdown + Menu submenu).
 *
 * - `click`: toggles; closes on outside pointer-down, Esc, or selecting an item.
 * - `hover`: opens on enter, closes shortly after leave. A transparent bridge
 *   between trigger and panel keeps it open while the pointer crosses the gap.
 */
export function Dropdown({
  children,
  panel,
  menu,
  trigger = 'click',
  align,
  placement,
  disabled,
  panelClassName,
  className,
}: DropdownProps) {
  const [open, setOpen] = useState(false);
  const triggerMode: 'click' | 'hover' = Array.isArray(trigger) ? (trigger.includes('hover') ? 'hover' : 'click') : trigger;
  // Honor antd `placement="bottomRight"` (and align="end") → right-align the panel.
  const alignSide: 'start' | 'end' = align ?? (placement && /right|End/i.test(placement) ? 'end' : 'start');
  // Build a panel from a declarative `menu` when provided.
  const menuPanel = menu ? (
    <>
      {(menu.items ?? []).filter(Boolean).map((raw, i) => {
        const it = raw as MenuItemSpec;
        if (it.type === 'divider') return <div key={i} className="my-1 h-px bg-line" />;
        return (
          <button
            key={it.key ?? i}
            type="button"
            disabled={it.disabled}
            onClick={() => { if (it.disabled) return; it.onClick?.(); if (it.key != null) menu.onClick?.({ key: it.key }); }}
            className={cn(
              'flex w-full items-center gap-2 rounded-control px-2.5 py-1.5 text-left text-sm transition-colors',
              it.danger ? 'text-danger-600 hover:bg-danger-500/10' : 'text-content hover:bg-surface-2',
              it.disabled && 'cursor-not-allowed opacity-50',
            )}
          >
            {it.icon != null && <span className="[&>svg]:size-4">{it.icon}</span>}
            {it.label}
          </button>
        );
      })}
    </>
  ) : null;
  const content = menu ? menuPanel : panel;
  const wrapRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Clean up any pending hover-close timer on unmount.
  useEffect(() => () => clearTimeout(closeTimer.current), []);

  /*
   * KEEP THE OPEN PANEL ON SCREEN.
   *
   * The panel hangs off its trigger's left or right edge. On a desktop bar that is right; on a phone a
   * trigger that is not at the screen edge pushes a wide panel past it — the notification bell sits left
   * of the avatar and menu, so its 360px panel ran 119px off the left of a 360px screen and the start of
   * every notification was cut. Measured once it opens, again after its pop-in animation, and on every
   * resize, the panel is nudged back inside an 8px gutter. A panel that already fits is not moved.
   */
  const panelRef = useRef<HTMLDivElement>(null);
  const nudgeRef = useRef(0);
  const [nudge, setNudge] = useState(0);
  useLayoutEffect(() => {
    if (!open) { nudgeRef.current = 0; setNudge(0); return; }
    const fit = () => {
      const el = panelRef.current;
      if (!el) return;
      const gutter = 8;
      const viewport = document.documentElement.clientWidth;
      const r = el.getBoundingClientRect();
      const left = r.left - nudgeRef.current; // where the panel sits before any nudge
      let dx = 0;
      if (left + r.width > viewport - gutter) dx = viewport - gutter - (left + r.width);
      if (left + dx < gutter) dx = gutter - left;
      if (dx !== nudgeRef.current) { nudgeRef.current = dx; setNudge(dx); }
    };
    fit();
    const settle = setTimeout(fit, 220);
    window.addEventListener('resize', fit);
    return () => { clearTimeout(settle); window.removeEventListener('resize', fit); };
  }, [open]);

  const hoverHandlers =
    triggerMode === 'hover' && !disabled
      ? {
          onMouseEnter: () => {
            clearTimeout(closeTimer.current);
            setOpen(true);
          },
          onMouseLeave: () => {
            closeTimer.current = setTimeout(() => setOpen(false), 130);
          },
        }
      : {};

  return (
    <div ref={wrapRef} className={cn('relative inline-flex', className)} {...hoverHandlers}>
      <div
        className={cn('inline-flex', disabled && 'pointer-events-none opacity-50')}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-disabled={disabled}
        // click toggles; hover-menus also open on tap so touch devices (no hover) can reach them.
        onClick={disabled ? undefined : triggerMode === 'click' ? () => setOpen((o) => !o) : () => setOpen(true)}
      >
        {children}
      </div>

      {open && !disabled && (
        <div
          className={cn('absolute top-full z-50 pt-2', alignSide === 'end' ? 'right-0' : 'left-0')}
          style={nudge ? { transform: `translateX(${nudge}px)` } : undefined}
          onClick={() => setOpen(false)}
        >
          <div
            ref={panelRef}
            role="menu"
            // Never wider than the screen, whatever width the caller asked for.
            style={{ maxWidth: 'calc(100vw - 1rem)' }}
            className={cn(
              // `space-y-0.5` puts a small gap between rows so a selected/hovered item's highlight
              // doesn't butt up against its neighbours.
              'min-w-[210px] space-y-0.5 rounded-card border border-line bg-surface p-1.5 shadow-pop',
              'animate-pop-in',
              panelClassName,
            )}
          >
            {content}
          </div>
        </div>
      )}
    </div>
  );
}
